import { z } from 'zod';
import { apiClient } from '../shared/api-client';
import { currentContactCreationScope, isCurrentContactCreationScope, readPendingContactCreation, resolvePendingContactCreation, submitContactCreation } from '../mobile/navigation/components/vrs/contactCreationRecovery';
const directorySchema = z.object({ result: z.object({
        id: z.string().trim().min(1).max(120),
        name: z.string().nullable().optional(), handle: z.string().nullable().optional(),
        phone_number: z.string().nullable().optional(), existing_contact: z.boolean().optional()
    }) });
/** Map the server's directory row once for both Home renderers. */
export async function lookupHomeDirectory(query) {
    const scope = currentContactCreationScope();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
        const response = await apiClient.get(`/api/contacts/lookup?query=${encodeURIComponent(query)}`, { retryAuth: false, signal: controller.signal });
        if (!isCurrentContactCreationScope(scope))
            throw new Error('Your account changed.');
        if (response.status === 404)
            return null;
        if (response.error)
            throw new Error(response.error);
        const parsed = directorySchema.safeParse(response.data);
        if (!parsed.success)
            throw new Error('Contact not confirmed. Try again.');
        const row = parsed.data.result;
        return { clientId: row.id, name: row.name || row.handle || query, handle: row.handle,
            phoneNumber: row.phone_number, existingContact: Boolean(row.existing_contact) };
    }
    finally {
        clearTimeout(timer);
    }
}
export const emptyHomeContactRequestState = () => ({
    ready: false, busy: false, status: '', pending: null, review: null, sentTarget: ''
});
export const homeContactTargetKey = (query, match) => match && !match.existingContact ? JSON.stringify([query.trim(), match.clientId, ...(match.source ? [match.source] : [])]) : '';
export function earlierHomeContactLabel(pending) {
    return pending.input?.kind === 'request' ? `Earlier request to ${pending.query || 'the original recipient'}`
        : pending.input?.kind === 'manual' ? `Earlier save for ${pending.input.contact.name}` : 'Earlier contact save';
}
function confirmedMessage(creation) {
    if (creation.kind === 'manual')
        return creation.contact ? 'The earlier contact is saved.' : 'The earlier save completed; that contact is no longer available.';
    if (!creation.request)
        return 'The earlier request completed; it is no longer available.';
    const messages = { pending: 'Contact request sent.', accepted: 'Contact request accepted.', rejected: 'Contact request declined.', cancelled: 'Contact request cancelled.' };
    return messages[creation.request.status];
}
function manualContactDraftKey(contact) {
    return JSON.stringify([
        contact.linkedClientId ?? null,
        contact.name.trim(),
        contact.email ?? null,
        contact.phoneNumber ?? null,
        contact.organization ?? null,
        contact.notes ?? null,
        contact.avatarColor ?? null,
        contact.isFavorite ?? null,
        [...(contact.groupIds || [])].sort()
    ]);
}
/**
 * Standalone call history has only a one-tap manual save. Reuse the same
 * durable operation as Contacts, and retry only when the frozen draft is the
 * exact history person the user selected again.
 */
export async function saveHomeHistoryContact(contact, canClearRecovery = () => true) {
    const scope = currentContactCreationScope();
    const pending = await readPendingContactCreation(scope);
    let expectedOperationId;
    if (pending) {
        if (pending.input?.kind !== 'manual') {
            throw new Error('Finish the earlier contact request before saving this call.');
        }
        if (manualContactDraftKey(pending.input.contact) !== manualContactDraftKey(contact)) {
            throw new Error('Finish the earlier contact save from its original call before saving another person.');
        }
        expectedOperationId = pending.operationId;
    }
    const outcome = await submitContactCreation(scope, { kind: 'manual', contact }, () => { }, expectedOperationId, canClearRecovery);
    if (outcome.creation.kind !== 'manual')
        throw new Error('Contact save not confirmed. Try again.');
    return { contact: outcome.creation.contact, cleanupPending: outcome.cleanupPending };
}
/** Home owns presentation; the existing shared service owns durable writes and reconciliation. */
export class HomeContactRequestController {
    constructor(onChange) {
        this.onChange = onChange;
        this.state = emptyHomeContactRequestState();
        this.scope = null;
        this.active = false;
        this.generation = 0;
        this.locked = false;
        this.target = { query: '', match: null };
    }
    publish(patch) {
        this.state = { ...this.state, ...patch };
        if (this.active)
            this.onChange(this.state);
    }
    /** Call on every view/query/account change, including close and unmount. */
    setContext(active, query, match) {
        const oldTarget = homeContactTargetKey(this.target.query, this.target.match);
        this.target = { query: query.trim(), match };
        let scope = null;
        if (active) {
            try {
                scope = currentContactCreationScope();
            }
            catch { /* Report below without publishing another account's draft. */ }
        }
        const changed = active !== this.active || scope?.clientId !== this.scope?.clientId || scope?.origin !== this.scope?.origin;
        this.active = active;
        if (changed) {
            this.generation++;
            this.scope = scope;
            this.state = emptyHomeContactRequestState();
            if (active) {
                this.publish({ busy: this.locked, status: scope ? '' : 'Session expired. Sign in again.' });
                if (scope && !this.locked)
                    void this.refresh();
            }
        }
        else if (oldTarget !== homeContactTargetKey(this.target.query, this.target.match) && !this.state.pending) {
            this.publish({ status: '', sentTarget: '' });
        }
    }
    current(generation, scope) {
        return this.active && generation === this.generation && isCurrentContactCreationScope(scope);
    }
    async run(work, canReportError = () => true) {
        const scope = this.scope, generation = this.generation;
        if (this.locked || !scope || !this.current(generation, scope))
            return;
        this.locked = true;
        this.publish({ busy: true });
        const current = () => this.current(generation, scope);
        try {
            await work(scope, current);
        }
        catch (error) {
            if (current() && canReportError())
                this.publish({ status: error instanceof Error ? error.message : 'The request could not be checked. Try again.' });
        }
        finally {
            this.locked = false;
            if (current())
                this.publish({ busy: false });
            else if (this.active && this.scope && isCurrentContactCreationScope(this.scope))
                void this.refresh();
        }
    }
    async refresh() {
        await this.run(async (scope, current) => {
            this.publish({ ready: false, status: 'Checking earlier saves…', review: null });
            const pending = await readPendingContactCreation(scope);
            if (current())
                this.publish({ ready: true, pending, status: '' });
        });
    }
    async finish(scope, current, message) {
        // Another pending operation may remain. Do not enable a new send until all are checked.
        const pending = await readPendingContactCreation(scope);
        if (current())
            this.publish({ ready: true, pending, review: null, status: message });
    }
    async send() {
        const { query, match } = this.target;
        const targetKey = homeContactTargetKey(query, match);
        if (!this.state.ready || this.state.pending || !match || !targetKey || this.state.sentTarget === targetKey)
            return;
        await this.run(async (scope, current) => {
            this.publish({ status: 'Sending contact request…' });
            const sameTarget = () => current() && homeContactTargetKey(this.target.query, this.target.match) === targetKey;
            const outcome = await submitContactCreation(scope, match.source === 'callParticipant'
                ? { kind: 'participantRequest', recipientClientId: match.clientId, displayName: match.name }
                : { kind: 'request', query, message: '', expectedRecipientClientId: match.clientId }, pending => { if (current())
                this.publish({ pending }); }, undefined, sameTarget);
            if (!current())
                return;
            if (outcome.cleanupPending) {
                this.publish({ status: sameTarget() ? 'Request confirmed. Recovery not cleared on this device.' : '' });
                return;
            }
            this.publish({ ready: false, sentTarget: sameTarget() ? targetKey : '' });
            await this.finish(scope, current, sameTarget() ? confirmedMessage(outcome.creation) : '');
        }, () => Boolean(this.state.pending) || homeContactTargetKey(this.target.query, this.target.match) === targetKey);
    }
    /** Explicitly names and retries the frozen earlier recipient, never the new typed query. */
    async retry() {
        const pending = this.state.pending;
        if (!this.state.ready || pending?.input?.kind !== 'request')
            return;
        const input = pending.input;
        await this.run(async (scope, current) => {
            this.publish({ status: 'Retrying the original contact request…' });
            const outcome = await submitContactCreation(scope, { kind: 'request', query: pending.query || '', message: input.request.message || '', expectedRecipientClientId: input.request.recipientClientId }, value => { if (current())
                this.publish({ pending: value }); }, pending.operationId, current);
            if (!current())
                return;
            if (outcome.cleanupPending) {
                this.publish({ status: 'Request confirmed. Recovery not cleared on this device.' });
                return;
            }
            this.publish({ ready: false });
            await this.finish(scope, current, `${earlierHomeContactLabel(pending)}: ${confirmedMessage(outcome.creation)}`);
        });
    }
    /** Confirmation also permanently stops an attempt that did not complete; the UI explains this. */
    async resolve(acceptSavedDetails = false) {
        const pending = this.state.pending;
        if (!this.state.ready || !pending || (acceptSavedDetails && !this.state.review))
            return;
        await this.run(async (scope, current) => {
            this.publish({ status: 'Checking the earlier save…' });
            const outcome = await resolvePendingContactCreation(scope, pending, acceptSavedDetails ? this.state.review : null, current);
            if (!current())
                return;
            if (outcome.reviewRequired && outcome.resolution.state === 'completed' && outcome.resolution.creation.kind === 'manual') {
                this.publish({ review: outcome.resolution.creation.contact, status: acceptSavedDetails
                        ? 'The saved contact changed after your review. Review it again.'
                        : 'This contact already has different saved details.' });
                return;
            }
            if (outcome.cleanupPending) {
                this.publish({ status: 'Outcome confirmed. Recovery not cleared on this device.' });
                return;
            }
            const message = outcome.resolution.state === 'abandoned'
                ? 'The earlier attempt was stopped.' : confirmedMessage(outcome.resolution.creation);
            this.publish({ ready: false });
            await this.finish(scope, current, `${earlierHomeContactLabel(pending)}: ${message}`);
        });
    }
}
